import { Button, Dialog, Text, VSpacer } from '@/components/DesignSystem';
import { PointsAuditApi } from '@/utilities/api/PointsAuditApi';
import {
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { PointsAuditEndpoint } from '@api/endpoints';
import { PointsMovementReason, RewardsProgramType } from '@shared/enums';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { DETAIL_DIALOG_WIDTH, scrollingTableSx } from './auditLayout';
import { describeMovementReason, formatSignedPoints } from './movementReasons';
import { programTypeLabel } from './programTypeLabel';
import { walletDetail } from './walletDetail';

interface FarmerLedgerDialogProps {
  farmerId: string,
  farmerName: string,
  onClose: () => void,
  processId: string,
}

const PAGE_LIMIT = 50;
const NO_WRAP = { whiteSpace: 'nowrap' } as const;

type LedgerRow = PointsAuditEndpoint.FarmerLedger.LedgerRow;

/** The name beside a reason: the promotion it came from, or a manual movement's note. */
const reasonDetail = (row: LedgerRow) => {
  if (row.reason === PointsMovementReason.Promotion) { return row.promotionName; }
  if (row.reason === PointsMovementReason.Manual) { return row.description; }
  return null;
};

/** The individual movements behind one farmer's line in a process's impact. */
export const FarmerLedgerDialog = ({
  farmerId,
  farmerName,
  onClose,
  processId,
}: FarmerLedgerDialogProps) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const { data, isFetching } = useQuery(
    ['points-audit-farmer-ledger', processId, farmerId, page],
    () => PointsAuditApi.farmerLedger(processId, farmerId, { limit: PAGE_LIMIT, page }),
    { keepPreviousData: true, retry: false },
  );

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const showProgram = rows.some(({ programType }) => programType === RewardsProgramType.Energy);

  const body = () => {
    if (isFetching) { return <CircularProgress size={20} />; }
    if (!rows.length) {
      return <Text category="body-small">{t($ => $.pointsAudit.ledger.none)}</Text>;
    }

    return (
      <TableContainer component={Paper} sx={scrollingTableSx(420)}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t($ => $.pointsAudit.ledger.orderDate)}</TableCell>
              <TableCell>{t($ => $.pointsAudit.ledger.orderErpId)}</TableCell>
              {showProgram && <TableCell>{t($ => $.pointsAudit.processes.program)}</TableCell>}
              <TableCell>{t($ => $.pointsAudit.reason.why)}</TableCell>
              <TableCell>{t($ => $.pointsAudit.ledger.product)}</TableCell>
              <TableCell>{t($ => $.pointsAudit.revert.wallet)}</TableCell>
              <TableCell>{t($ => $.pointsAudit.ledger.tier)}</TableCell>
              <TableCell align="right">{t($ => $.pointsAudit.ledger.amount)}</TableCell>
              <TableCell align="right">{t($ => $.pointsAudit.ledger.balance)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow data-testid={`farmer-ledger-row-${row.id}`} key={row.id}>
                {/* A manual movement's date is when it was entered, not an order's. */}
                <TableCell sx={NO_WRAP}>
                  {row.orderExternalId
                    ? t($ => $.pointsAudit.ledger.orderDateValue, { date: row.orderDate })
                    : '—'}
                </TableCell>
                <TableCell>{row.orderExternalId ?? '—'}</TableCell>
                {showProgram && <TableCell>{programTypeLabel(row.programType, t)}</TableCell>}
                <TableCell data-testid={`farmer-ledger-reason-${row.id}`}>
                  <Stack alignItems="flex-start" gap={0.5}>
                    <Chip
                      label={describeMovementReason(row.reason, t)}
                      size="small"
                      variant="outlined"
                    />
                    {!!reasonDetail(row) && (
                      <Text category="body-small">{reasonDetail(row)}</Text>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{row.productName ?? '—'}</TableCell>
                <TableCell>
                  <Text category="body-medium">{row.subwalletName ?? '—'}</Text>
                  {!!walletDetail({ ...row, expirationDate: row.walletExpirationDate }, t) && (
                    <Text category="body-small" sx={{ color: 'text.secondary' }}>
                      {walletDetail({ ...row, expirationDate: row.walletExpirationDate }, t)}
                    </Text>
                  )}
                </TableCell>
                <TableCell data-testid={`farmer-ledger-tier-${row.id}`}>
                  {row.tierName ?? (row.tierRank === null ? '—' : `#${row.tierRank}`)}
                </TableCell>
                <TableCell align="right" sx={NO_WRAP}>{formatSignedPoints(row.amount)}</TableCell>
                <TableCell align="right" sx={NO_WRAP}>
                  {row.beforeBalance ?? '—'} → {row.afterBalance ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const pager = () => (
    total > PAGE_LIMIT && (
      <>
        <VSpacer size="3" />
        <Stack alignItems="center">
          <Pagination
            count={Math.ceil(total / PAGE_LIMIT)}
            onChange={(_, next) => setPage(next - 1)}
            page={page + 1}
          />
        </Stack>
      </>
    )
  );

  return (
    <Dialog
      cancelButton={() => (
        <Button onClick={onClose} testID="farmer-ledger-close">
          {t($ => $.common.actions.close)}
        </Button>
      )}
      dialogWidth={DETAIL_DIALOG_WIDTH}
      onClose={onClose}
      open
      testID="farmer-ledger-dialog"
      title={t($ => $.pointsAudit.ledger.title, { farmer: farmerName })}
    >
      <VSpacer size="2" />
      {body()}
      {pager()}
    </Dialog>
  );
};
